import { useState, type FormEvent } from 'react'
import { AssigneePicker } from './AssigneePicker'
import { PLAN_GROUPS } from '../types'
import type { PlanTask, PlanGroup, PlanPriority } from '../types'

// Create/edit modal for an EventPlanTask. One form, two callers — `mode`
// distinguishes "create" (initial=null, button reads "Add task") from
// "edit" (initial=task, button reads "Save changes" + Delete shown).
//
// Reuses AssigneePicker variant="field" — same primitive as the Actions
// editor (PR #21) so picking an owner feels identical across surfaces.
//
// Out of scope (deferred to follow-ups, surfaced in PR description):
//   - dependsOnIds multi-select. EventPlanTaskDeps schema is there;
//     wire shape + Gantt arrow renderer aren't.

interface EditorBody {
  name: string
  description: string | null
  group: PlanGroup
  assigneeId: number | null
  startAt: string         // YYYY-MM-DD
  endAt: string           // YYYY-MM-DD
  progress: number        // 0..100
  priority: PlanPriority
  notes: string | null
}

interface Props {
  mode: 'create' | 'edit'
  initial: PlanTask | null
  saving: boolean
  deleting?: boolean
  serverError: Error | null
  defaultStartAt?: string  // YYYY-MM-DD for create when no other signal
  onClose: () => void
  onSave: (body: EditorBody) => void
  onDelete?: () => void    // only wired when mode='edit'
}

export function PlanTaskEditor({
  mode, initial, saving, deleting, serverError,
  defaultStartAt, onClose, onSave, onDelete
}: Props) {
  // For new tasks default to a 1-week window starting today (or the
  // caller-supplied default — the parent passes the event start_date so
  // a brand-new event's first task lands inside the event window).
  const fallbackStart = defaultStartAt ?? todayIso()
  const fallbackEnd = addDays(fallbackStart, 7)

  const [name, setName]               = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [group, setGroup]             = useState<PlanGroup>(initial?.group ?? 'Bookings')
  const [assigneeId, setAssigneeId]   = useState<number | null>(initial?.assigneeId ?? null)
  const [startAt, setStartAt]         = useState(initial?.startAt ?? fallbackStart)
  const [endAt, setEndAt]             = useState(initial?.endAt ?? fallbackEnd)
  const [progress, setProgress]       = useState<number>(initial?.progress ?? 0)
  const [priority, setPriority]       = useState<PlanPriority>(initial?.priority ?? 'medium')
  const [notes, setNotes]             = useState(initial?.notes ?? '')

  const trimmedName = name.trim()
  const fieldErrors: Partial<Record<keyof EditorBody, string>> = {}
  if (trimmedName.length === 0) fieldErrors.name = 'Name is required'
  else if (trimmedName.length > 200) fieldErrors.name = 'Name must be 200 characters or fewer'
  if (!startAt) fieldErrors.startAt = 'Start date is required'
  if (!endAt) fieldErrors.endAt = 'End date is required'
  if (startAt && endAt && endAt < startAt) fieldErrors.endAt = 'End must be on or after start'
  if (!Number.isInteger(progress) || progress < 0 || progress > 100) {
    fieldErrors.progress = 'Progress must be 0..100'
  }
  const isValid = Object.keys(fieldErrors).length === 0

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    onSave({
      name: trimmedName,
      description: description.trim() || null,
      group,
      assigneeId,
      startAt,
      endAt,
      progress,
      priority,
      notes: notes.trim() || null
    })
  }

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center p-5"
      style={{ background: 'rgba(31, 29, 24, 0.55)', backdropFilter: 'blur(4px)' }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-lg shadow-xl w-full max-w-xl my-8 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl">
            {mode === 'create' ? 'New plan task' : 'Edit plan task'}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink text-xl">×</button>
        </div>

        <div className="space-y-3">
          <Label text="Name" error={fieldErrors.name}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              autoFocus
              required
              className={inputClass(!!fieldErrors.name)}
            />
          </Label>

          <Label text="Description (optional)">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={inputClass(false)}
            />
          </Label>

          <div className="grid grid-cols-2 gap-3">
            <Label text="Category">
              <select
                value={group}
                onChange={(e) => setGroup(e.target.value as PlanGroup)}
                className={inputClass(false)}
              >
                {PLAN_GROUPS.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </Label>
            <Label text="Priority">
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as PlanPriority)}
                className={inputClass(false)}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </Label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Label text="Start" error={fieldErrors.startAt}>
              <input
                type="date"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                className={inputClass(!!fieldErrors.startAt)}
              />
            </Label>
            <Label text="End" error={fieldErrors.endAt}>
              <input
                type="date"
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
                className={inputClass(!!fieldErrors.endAt)}
              />
            </Label>
          </div>

          <AssigneePicker
            variant="field"
            value={assigneeId}
            onChange={setAssigneeId}
            disabled={saving}
          />

          <Label text="Progress" error={fieldErrors.progress} hint={`${progress}%`}>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={progress}
              onChange={(e) => setProgress(Number(e.target.value))}
              className="w-full"
              disabled={saving}
            />
          </Label>

          <Label text="Notes (optional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className={inputClass(false)}
            />
          </Label>

          {serverError && (
            <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-2 text-xs">
              {serverError.message}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-line">
          {mode === 'edit' && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={saving || deleting}
              className="mr-auto px-4 py-2 rounded text-sm font-semibold text-oxblood hover:bg-oxblood-soft disabled:opacity-50"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded text-sm text-ink-soft hover:bg-parchment"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isValid || saving}
            className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : mode === 'create' ? 'Add task' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  )
}

// ---- Helpers -------------------------------------------------------------

function Label({
  text, error, hint, children
}: {
  text: string
  error?: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft">{text}</span>
        {hint && <span className="text-xs text-ink-soft font-mono">{hint}</span>}
      </div>
      {children}
      {error && <div className="text-xs text-oxblood mt-1">{error}</div>}
    </label>
  )
}

function inputClass(invalid: boolean): string {
  return (
    'w-full border rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 transition-colors ' +
    (invalid
      ? 'border-oxblood focus:border-oxblood focus:ring-oxblood/30'
      : 'border-line focus:border-green focus:ring-green/30')
  )
}

function todayIso(): string {
  const d = new Date()
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  const t = new Date(Date.UTC(y, m - 1, d))
  t.setUTCDate(t.getUTCDate() + n)
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`
}
